import { useCallback } from "react";

import { serverResponseFormat } from "@similarweb/pro-logic-components";

import { useApiMutation, useApiQuery } from "app/modules/api-client";
import { DateFormatter } from "app/pages/digital-marketing/common/services/formatters/dateFormatter";

import { DEFAULT_MAX_EXCEL_ROWS } from "../../shared/data/component-config/component-config";
import { useMonitorAndProtectTrademarkDataAndParams } from "../../shared/hooks/trademark-params";

// Re-export query key for backward compatibility
export { TRADEMARKS_ADS_DAILY_CREATE_QUERY_KEY } from "./query-keys";

const STALE_CONFIG = {
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
    refetchOnReconnect: true,
};

export const useGetTrademarks = () => {
    return useApiQuery({
        tag: "MonitorAndProtectMaster",
        methodName: "monitorandprotectTrademarksList",
        options: {
            ...STALE_CONFIG,
        },
    });
};

export const useGetTrademarkById = (id?: string) => {
    return useApiQuery(
        {
            tag: "MonitorAndProtectMaster",
            methodName: "monitorandprotectTrademarksDetail",
            options: {
                enabled: !!id,
                ...STALE_CONFIG,
            },
        },
        id as string,
    );
};

export const useCreateTrademark = () => {
    return useApiMutation("MonitorAndProtectMaster", "monitorandprotectTrademarksCreate", {
        invalidateQueries: {
            MonitorAndProtectMaster: {
                monitorandprotectTrademarksList: true,
                monitorandprotectTrademarksAdsDailyCreate: true,
            },
        },
    });
};

export const usePatchTrademark = () => {
    return useApiMutation("MonitorAndProtectMaster", "monitorandprotectTrademarksPartialUpdate", {
        invalidateQueries: {
            MonitorAndProtectMaster: {
                monitorandprotectTrademarksList: true,
                monitorandprotectTrademarksDetail: true,
                monitorandprotectTrademarksAdsDailyCreate: true,
            },
        },
    });
};

export const useDeleteTrademark = () => {
    return useApiMutation("MonitorAndProtectMaster", "monitorandprotectTrademarksDelete", {
        invalidateQueries: {
            MonitorAndProtectMaster: {
                monitorandprotectTrademarksList: true,
                monitorandprotectTrademarksAdsDailyCreate: true,
            },
        },
    });
};
export const useGetTrademarkAds = (pagination?: { pageSize: number; page: number }, disabled?: boolean) => {
    const { duration, trademarkId, sortBy, sortDir, dataFilters } = useMonitorAndProtectTrademarkDataAndParams();
    const date = DateFormatter.parseDurationToDateRange(duration, serverResponseFormat);

    return useApiQuery(
        {
            tag: "MonitorAndProtectMaster",
            methodName: "monitorandprotectTrademarksAdsDailyCreate",
            options: {
                ...STALE_CONFIG,
                enabled: !!trademarkId && !disabled,
            },
        },
        {
            fromDate: date.from,
            toDate: date.to,
            page: pagination?.page,
            pageSize: pagination?.pageSize,
            sort: sortBy,
            asc: sortDir === "ascend",
        },
        {
            device: dataFilters?.deviceType || "All",
            campaignIds: dataFilters?.campaignsIds,
            trademarkIds: trademarkId ? [trademarkId] : undefined,
            locations: dataFilters?.locations?.map(Number),
            violationTypes: dataFilters?.violationType ? [dataFilters?.violationType] : undefined,
            includeExcludeAdvertiserRules: {
                isOneOf: dataFilters?.domains?.include,
                isNotOneOf: dataFilters?.domains?.exclude,
            },
        },
    );
};

export function useMonitorAndProtectTrademarkAdExcel() {
    const { sortBy, sortDir, dataFilters, duration, trademarkId } = useMonitorAndProtectTrademarkDataAndParams();
    const { deviceType, campaignsIds, locations, violationType, domains } = dataFilters ?? {};
    const date = DateFormatter.parseDurationToDateRange(duration, serverResponseFormat);

    const { mutateAsync, isPending } = useApiMutation(
        "MonitorAndProtectMaster",
        "monitorandprotectTrademarksAdsDailyExcelCreate",
    );

    const getExcel = useCallback(
        (rowCount: number = DEFAULT_MAX_EXCEL_ROWS) => {
            return mutateAsync([
                {
                    fromDate: date.from,
                    toDate: date.to,
                    sort: sortBy,
                    asc: sortDir === "ascend",
                },
                {
                    device: deviceType || "All",
                    campaignIds: campaignsIds,
                    trademarkIds: trademarkId ? [trademarkId] : undefined,
                    locations: locations?.map(Number),
                    violationTypes: violationType ? [violationType] : undefined,
                    includeExcludeAdvertiserRules: {
                        isOneOf: domains?.include,
                        isNotOneOf: domains?.exclude,
                    },
                    maxRows: rowCount,
                },
                {
                    format: "blob",
                    headers: {
                        Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    },
                },
            ]);
        },
        [
            mutateAsync,
            date.from,
            date.to,
            sortBy,
            sortDir,
            deviceType,
            campaignsIds,
            trademarkId,
            locations,
            violationType,
            domains?.include,
            domains?.exclude,
        ],
    );

    return { getExcel, isLoading: isPending };
}

export function useMonitorAndProtectTrademarkAdExcelDownload() {
    const { getExcel, isLoading } = useMonitorAndProtectTrademarkAdExcel();

    const downloadExcel = useCallback(
        async (rowCount?: number) => {
            await getExcel(rowCount)
                .then((response: Blob) => {
                    const url = window.URL.createObjectURL(response);
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", "trademark_ads_report.xlsx");
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode?.removeChild(link);
                })
                .catch((error) => {
                    console.error("Error downloading the Excel report:", error);
                    throw error;
                });
        },
        [getExcel],
    );

    return { downloadExcel, isLoading };
}
